import $ from 'gridstrap/lib/gs-zepto';
import analytics, { invalidReason, stepVariant, whenSeen, type StepRef } from 'gridstrap/lib/gs-analytics';
import * as time from 'gridstrap/lib/time';
import { modal } from '../modal';

declare var ga;

// How long a submit waits for the phone verdicts before posting anyway.
const PHONE_CHECK_TIMEOUT_MS = 3000;

// GSFormError renders its message as HTML; a server reason is text.
function escapeHtml(text) {
	return text.replace(/[&<>"']/g, c => ({
		'&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'
	})[c]);
}

export class GSForm {

	//
	// Properties
	//

	private root;
	private fields;
	private isCheckingPhones = false;

	get endpoint() { return '/api/' + this.root.data('gsFormEndpoint'); }
	get method() { return this.root.data('gsFormMethod'); }
	get data() { return this.root.serialize(); }

	get gaCategory() { return this.root.data('gsFormGaCategory'); }
	get gaLabel()    { return this.root.data('gsFormGaLabel'); }
	// Funnel telemetry (SC-620): an absent attribute means track everything;
	// a declared list still narrows.
	get gaActions()  {
		const actions = this.root.data('gsFormGaActions');
		return actions ? String(actions).split(' ') : null;
	}

	// The wizard step this form is on: the step holding it, or its own active
	// step. A form with neither is a one-step surface.
	get step() {
		const holder = this.root.closest('.gs-Wizard-step');
		if (holder.length > 0) return holder.gs(0);
		return this.root.find('.gs-Wizard-step.is-active').gs(0);
	}

	get hasWizard() {
		return this.root.closest('.gs-Wizard').length > 0 || this.root.find('.gs-Wizard').length > 0;
	}

	get stepRef(): StepRef {
		const step = this.step;
		if (step) return step.ref;
		return { stepNumber: 1, stepName: null, stepVariant: stepVariant(this.root.get(0)) };
	}

	get submitButtons() { return this.root.find('.gs-Button[type=submit]'); }
	get errors() { return this.root.find('.gs-FormError').gs(0); }

	get isEmbedded() { return this.root.hasClass('gs-Form--embedded'); }
	get isEngaged() { return this.root.hasClass('is-engaged'); }
	get isDirty() { return this.fields.gs('isDirty').includes(true); }
	get isDisabled() { return this.root.hasClass('is-disabled'); }

	set isEngaged(value) { this.root.toggleClass('is-engaged', !!value); }
	set isDisabled(value) { this.root.toggleClass('is-disabled', !!value); }
	set isSubmitted(value) { this.root.toggleClass('is-submitted', !!value); }

	set isProcessing(value) {
		this.root.toggleClass('is-processing', !!value);
		this.submitButtons.gs('isProcessing', !!value);
	}

	//
	// Methods
	//

	// The legacy action names map onto the funnel vocabulary (ADR-0004). In a
	// wizard the steps report their own views and engagement; the form only
	// reports what it alone sees, an invalid final submit. The submit row
	// itself is written by the api, never beaconed.
	track(eventAction, invalidField?) {
		if (this.gaActions && !this.gaActions.includes(eventAction)) return;
		const root = this.root.get(0);
		const { stepNumber } = this.stepRef;
		if (eventAction == 'view' && !this.hasWizard) analytics.stepView(root, this.stepRef);
		if (eventAction == 'engage' && !this.hasWizard) analytics.engaged(root, stepNumber);
		if (eventAction == 'invalid') analytics.invalid(root, stepNumber, invalidReason(invalidField && invalidField.root.get(0)));
	}

	engage() {
		if (this.isEngaged) return;
		this.isEngaged = true;
		this.track('engage');
	}

	submit(evt) {
		evt.preventDefault();

		let wizard = this.root.find('.gs-Wizard');
		let step = wizard.find('.gs-Wizard-step.is-active');
		let fields = step.find('.gs-FormField');
		let submitButtons = step.find('.gs-Button[type="submit"]');
		let nextButton = step.find('[role~="next"]').first();

		if (step.length > 0 && submitButtons.length == 0) {
			fields.gs('validate');
			let valid = fields.gs().filter(field => field.isValid === true);
			if (valid) nextButton.trigger('click');
			return;
		}

		if (this.isCheckingPhones) return;
		this.isCheckingPhones = true;
		this.isProcessing = true;

		this.checkPhones().then(() => {
			this.isCheckingPhones = false;
			this.isProcessing = false;
			this.send();
		});
	}

	// Let every phone field fetch the worker's verdict before the final
	// validation (SC-616), so a typo is caught in place instead of by a 400.
	// Bounded: a slow or unreachable endpoint never holds a submit hostage.
	private checkPhones() {
		const pending = this.fields.gs()
			.filter(field => field && typeof field.checkPhone == 'function')
			.map(field => field.checkPhone());
		const timeout = new Promise(resolve => setTimeout(resolve, PHONE_CHECK_TIMEOUT_MS));
		return Promise.race([Promise.all(pending), timeout]);
	}

	private send() {
		this.fields.gs('validate');
		let invalid = this.fields.gs().filter(field => field.isValid === false);

		if (invalid.length > 0) {
			invalid.forEach(field => field.track('invalid'));
			this.track('invalid', invalid[0]);
			invalid[0].focus();
			return;
		}

		this.isDisabled = true;
		this.isProcessing = true;

		$['ajax']({
			url:     this.endpoint,
			type:    this.method,
			data:    this.dataWithFormIntent(),
			cache:   false,
			timeout: 10000,
			success: action => this.accept(action),
			error:   xhr => this.error(xhr)
		});
	}

	// Only POST /api/projects writes a `submit` row, so only it gets the
	// form-intent (as JSON; the worker parses it back out of the form body).
	private dataWithFormIntent() {
		if (this.endpoint != '/api/projects') return this.data;
		const ref = analytics.submitRef(this.root.get(0), this.stepRef);
		if (!ref) return this.data;
		return this.data + '&formIntent=' + encodeURIComponent(JSON.stringify(ref));
	}

	private accept(response) {
		if (!response || !response.errors) analytics.accepted(this.root.get(0));
		this.done(response);
	}

	done(response) {

		this.isSubmitted = true;
		this.submitButtons.gs('isProcessing', false);

		if (response.message) {
			alert(response.message);
		}

		if (response.action == 'redirect') {
			this.submitButtons.gs('isSuccess', true);
			return setTimeout(() => location.href = response.url, 1000);
		}

		if (response.action == 'refresh') {
			var noHashURL = window.location.href.replace(/#.*$/, '');
			window.history.replaceState('', document.title, noHashURL) ;
			this.submitButtons.gs('isSuccess', true);
			return setTimeout(() => window.location.reload(), 1000);
		}

		if (response.action == 'close') {
			setTimeout(() => modal.close(), 2000);
			this.submitButtons.gs('isSuccess', true);
			return;
		}

		if (response.action == 'jump') {
			let wizard = this.root.closest('.gs-Wizard').gs(0)
								|| this.root.find('.gs-Wizard').gs(0);

			this.submitButtons.gs('isSuccess', true);
			return setTimeout(() => {
				wizard.jump(response.target);
				this.isDisabled = false;
			}, 500);
		}

		if (response.errors) {
			this.showMessage(response.errors);
		}

		this.isProcessing = false;
		this.isDisabled = false;
	}

	// Say what the server said was wrong (SC-616). The apology is left for
	// responses that carry no reason: timeouts, network errors, 5xx pages.
	error(xhr?) {
		let reason = null;
		try {
			const body = JSON.parse(xhr.responseText);
			reason = body && (body.error || (Array.isArray(body.errors) && body.errors.join(' ')));
		} catch (e) {}

		if (typeof reason == 'string' && reason.trim() != '') {
			return this.done({ errors: [
				escapeHtml(reason.trim())
			+ " If this keeps happening, please call (800) 827-1151."
			]});
		}

		this.done({ errors: [
			"We're so sorry, but we encountered a problem submitting "
		+ "your request. Please call (800) 827-1151 for assistance."
		]});
	}

	showMessage(message) {
		if (this.errors) {
			this.errors.text = message;
		} else if (Array.isArray(message)) {
			alert(message.join('\n'));
		} else {
			alert(message);
		}
	}

	//
	// Initializations & Event Listeners
	//

	constructor(root) {
		this.root = $(root);
		this.fields = this.root.find('.gs-FormField');
		this.root.on('submit', evt => this.submit(evt));

		this.root.find('input, textarea, select, button')
			.one('click input change', () => this.engage());

		// A form outside a dialog is its own capture surface, and its
		// impression is scrolling into view. A dialog's is its opening
		// (modal-service.ts).
		if (this.root.closest('.gs-Modal').length == 0) {
			const root = this.root.get(0);
			const name = this.gaLabel || this.root.data('gsFormEndpoint') || 'form';
			analytics.surface(root, 'inline-form', String(name));
			whenSeen(root, () => analytics.impression(root));
		}
	}

	gsOnInit() {
		this.track('view');

		$('<input />')
			.attr('name', 'referringPage')
			.attr('type', 'hidden')
			.val(location.pathname)
			.appendTo(this.root);

		let timezone = time.timezone();
		if (timezone) {
			$('<input />')
				.attr('name', 'timezone')
				.attr('type', 'hidden')
				.val(timezone)
				.appendTo(this.root);
		}

		try {
			ga(tracker => {
				let clientId = tracker.get('clientId');
				if (clientId) {
					$('<input />')
						.attr('name', 'gaClientId')
						.attr('type', 'hidden')
						.val(clientId)
						.appendTo(this.root);
				}
			});
		} catch(e) {}
	}
}
